import { useState, useEffect } from "react";
import { fetchComparisons, fetchComparison } from "../api";

const SEV_ORDER = ["NONE", "LOW", "MEDIUM", "HIGH", "CRITICAL"];
const SEV_COLORS = { NONE: "#8b949e", LOW: "#7ee787", MEDIUM: "#d29922", HIGH: "#f0883e", CRITICAL: "#f85149" };

function ConfusionMatrix({ cm, labels, title }) {
  const [showPct, setShowPct] = useState(false);
  if (!cm || !labels) return null;

  // Compute row totals for percentages
  const rowTotals = {};
  labels.forEach(r => {
    rowTotals[r] = labels.reduce((sum, c) => sum + ((cm[r] || {})[c] || 0), 0);
  });

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 12, color: "var(--text-secondary)", marginBottom: 4, display: "flex", alignItems: "center", gap: 8 }}>
        {title}
        <button onClick={() => setShowPct(!showPct)}
          style={{ fontSize: 9, padding: "1px 6px", background: "none", border: "1px solid var(--border)",
                   borderRadius: 4, color: "var(--text-secondary)", cursor: "pointer" }}>
          {showPct ? "#" : "%"}
        </button>
      </div>
      <table style={{ borderCollapse: "collapse", fontSize: 11, fontFamily: "monospace" }}>
        <thead>
          <tr>
            <th style={{ padding: "2px 6px", color: "var(--text-muted)" }}></th>
            {labels.map(l => <th key={l} style={{ padding: "2px 6px", color: "var(--text-secondary)", minWidth: 40, textAlign: "right" }}>{l}</th>)}
          </tr>
        </thead>
        <tbody>
          {labels.map(r => {
            const total = rowTotals[r] || 1;
            return (
              <tr key={r}>
                <td style={{ padding: "2px 6px", color: "var(--text-secondary)", textAlign: "right", fontWeight: 500 }}>{r}</td>
                {labels.map(c => {
                  const v = (cm[r] || {})[c] || 0;
                  const pct = (v / total * 100);
                  const bg = r === c ? "rgba(126,231,135,0.12)" : v > 0 ? "rgba(248,81,73,0.08)" : "transparent";
                  const display = showPct ? (v ? pct.toFixed(0) + "%" : "") : (v || "");
                  return <td key={c} style={{ padding: "2px 6px", textAlign: "right", background: bg, color: v ? "var(--text-primary)" : "var(--text-muted)" }}>{display}</td>;
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ModelCard({ item, onSelect, selected }) {
  const acc = item.severity_accuracy ?? item.accuracy ?? 0;
  const accColor = acc >= 70 ? "#7ee787" : acc >= 50 ? "#d29922" : "#f85149";
  return (
    <div
      onClick={() => onSelect(item.file)}
      style={{
        padding: "10px 14px", borderRadius: 6, cursor: "pointer",
        border: selected ? "1px solid var(--accent)" : "1px solid var(--border)",
        background: selected ? "rgba(88,166,255,0.06)" : "var(--bg-secondary)",
        marginBottom: 6,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <span style={{ color: "var(--text-primary)", fontWeight: 500, fontSize: 13 }}>{item.model}</span>
          <span style={{ color: "var(--text-muted)", fontSize: 11, marginLeft: 8 }}>{item.type}</span>
        </div>
        <div style={{ textAlign: "right" }}>
          <span style={{ color: accColor, fontWeight: 600, fontSize: 15 }}>{acc}%</span>
          <span style={{ color: "var(--text-muted)", fontSize: 11, marginLeft: 6 }}>n={item.total?.toLocaleString()}</span>
        </div>
      </div>
      {item.mae != null && (
        <div style={{ color: "var(--text-muted)", fontSize: 11, marginTop: 2 }}>
          MAE: {item.mae} | Vector: {item.vector_accuracy ?? "-"}%
        </div>
      )}
    </div>
  );
}

export default function ComparePanel() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchComparisons()
      .then(data => { setItems(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!selected) { setDetail(null); return; }
    fetchComparison(selected)
      .then(setDetail)
      .catch(() => setDetail(null));
  }, [selected]);

  const types = [...new Set(items.map(i => i.type))];
  const filtered = filter === "all" ? items : items.filter(i => i.type === filter);
  const sorted = [...filtered].sort((a, b) => (b.severity_accuracy ?? b.accuracy ?? 0) - (a.severity_accuracy ?? a.accuracy ?? 0));

  if (loading) return <div style={{ padding: 24, color: "var(--text-secondary)" }}>Loading comparisons...</div>;
  if (!items.length) return <div style={{ padding: 24, color: "var(--text-secondary)" }}>No precomputed comparisons found. Run: python scripts/precompute-comparisons.py</div>;

  return (
    <div style={{ display: "flex", gap: 16, padding: "0 16px" }}>
      {/* Left: model list */}
      <div style={{ width: 380, flexShrink: 0 }}>
        <div style={{ marginBottom: 8, display: "flex", gap: 6 }}>
          <button onClick={() => { setFilter("all"); setSelected(null); }} className={`metrics-subtab ${filter === "all" ? "metrics-subtab-active" : ""}`}>All</button>
          {types.map(t => (
            <button key={t} onClick={() => { setFilter(t); setSelected(null); }} className={`metrics-subtab ${filter === t ? "metrics-subtab-active" : ""}`}>
              {t.replace("CVSSv", "CVSS ")}
            </button>
          ))}
        </div>
        <div style={{ maxHeight: "70vh", overflowY: "auto" }}>
          {sorted.map(item => (
            <ModelCard key={item.file} item={item} onSelect={setSelected} selected={selected === item.file} />
          ))}
        </div>
      </div>

      {/* Right: detail */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {!detail ? (
          <div style={{ padding: 24, color: "var(--text-muted)" }}>Select a model to view comparison details</div>
        ) : (
          <div>
            <h3 style={{ color: "var(--text-primary)", margin: "0 0 4px" }}>{detail.truth} vs {detail.model}</h3>
            <div style={{ color: "var(--text-muted)", fontSize: 12, marginBottom: 12 }}>{detail.type} | {detail.total?.toLocaleString()} CVEs | Days: {detail.days}</div>

            {/* Summary stats */}
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 16 }}>
              {detail.severity_accuracy != null && <Stat label="Severity Acc" value={`${detail.severity_accuracy}%`} />}
              {detail.vector_accuracy != null && <Stat label="Vector Acc" value={`${detail.vector_accuracy}%`} />}
              {detail.mae != null && <Stat label="MAE" value={detail.mae} />}
              {detail.mean_signed_error != null && <Stat label="Bias" value={detail.mean_signed_error > 0 ? `+${detail.mean_signed_error}` : detail.mean_signed_error} />}
              {detail.within_1 != null && <Stat label="Within 1.0" value={detail.within_1?.toLocaleString()} />}
              {detail.accuracy != null && detail.severity_accuracy == null && <Stat label="Accuracy" value={`${detail.accuracy}%`} />}
              {detail.exact_match != null && <Stat label="Exact Match" value={detail.exact_match?.toLocaleString()} />}
            </div>

            {/* Severity CM */}
            {detail.confusion_matrix && (
              <ConfusionMatrix cm={detail.confusion_matrix} labels={SEV_ORDER} title="Severity Confusion Matrix" />
            )}

            {/* Component accuracy */}
            {detail.components && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 12, color: "var(--text-secondary)", marginBottom: 4 }}>Component Accuracy</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {Object.entries(detail.components).map(([k, v]) => {
                    const acc = v.accuracy || 0;
                    const color = acc >= 80 ? "#7ee787" : acc >= 60 ? "#d29922" : "#f85149";
                    return (
                      <div key={k} style={{ background: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: 6, padding: "6px 10px", minWidth: 60, textAlign: "center" }}>
                        <div style={{ color: "var(--text-secondary)", fontSize: 10, fontWeight: 500 }}>{k}</div>
                        <div style={{ color, fontSize: 14, fontWeight: 600 }}>{acc}%</div>
                        <div style={{ color: "var(--text-muted)", fontSize: 9 }}>n={v.n?.toLocaleString()}</div>
                      </div>
                    );
                  })}
                </div>

                {/* Component CMs */}
                <div style={{ display: "flex", flexWrap: "wrap", gap: 16, marginTop: 12 }}>
                  {Object.entries(detail.components).map(([k, v]) => {
                    if (!v.confusion_matrix) return null;
                    const labels = [...new Set([
                      ...Object.keys(v.confusion_matrix),
                      ...Object.values(v.confusion_matrix).flatMap(r => Object.keys(r))
                    ])].sort();
                    return <ConfusionMatrix key={k} cm={v.confusion_matrix} labels={labels} title={k} />;
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div style={{ background: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: 6, padding: "8px 14px", textAlign: "center" }}>
      <div style={{ color: "var(--text-primary)", fontSize: 16, fontWeight: 600 }}>{value}</div>
      <div style={{ color: "var(--text-muted)", fontSize: 10 }}>{label}</div>
    </div>
  );
}
